import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout, setUserProfile } from './authSlice';
import { userService } from '@/services/userService';
import type { User, UserTemplateSettings } from '@/types';
import type { RootState } from '../index';
import { resolveTemplatesWithServerFallback } from '@/data/templates/templateDiffStorage';
import {
  DEFAULT_EMAIL_TEMPLATES,
  type EmailTemplateWithRef,
} from '@/data/templates/email/defaultEmailTemplates';
import {
  DEFAULT_CONTRACT_TEMPLATES,
  type ContractTemplateWithRef,
} from '@/data/templates/contracts/defaultContractTemplates';
import {
  DEFAULT_QUESTIONNAIRE_TEMPLATES,
  type QuestionnaireTemplateWithRef,
} from '@/data/templates/questionnaires/defaultQuestionnaireTemplates';
import {
  DEFAULT_PAYMENT_SCHEDULES,
  DEFAULT_QUOTE_TEMPLATES,
  type QuoteTemplateWithRef,
} from '@/data/templates/quotes/defaultQuoteTemplates';
import {
  DEFAULT_INVOICE_TEMPLATES,
  type InvoiceTemplateWithRef,
} from '@/data/templates/invoices/defaultInvoiceTemplates';
import { loadInvoiceDiscounts } from '@/data/invoicing/discountStorage';
import { loadProductPackages, saveProductPackages } from '@/data/invoicing/packageStorage';
import { loadTaxRates, saveTaxRates } from '@/data/invoicing/taxRateStorage';
import type {
  Discount,
  PaymentSchedule,
  ProductPackage,
  QuestionnaireField,
  TaxRate,
} from '@/pages/sidebar/SettingsPage/types';

interface TaxRateFormState {
  name: string;
  rate: string;
  inclusion: 'inclusive' | 'exclusive';
}

interface SettingsTemplatesState {
  userId: string | null;
  isSaving: boolean;
  isInitializing: boolean;
  isInitialized: boolean;
  error: string | null;
  taxRates: TaxRate[];
  productPackages: ProductPackage[];
  discounts: Discount[];
  paymentSchedules: PaymentSchedule[];
  quoteTemplates: QuoteTemplateWithRef[];
  defaultQuoteTemplateId: string | null;
  invoiceTemplates: InvoiceTemplateWithRef[];
  defaultInvoiceTemplateId: string | null;
  contractTemplates: ContractTemplateWithRef[];
  questionnaireTemplates: QuestionnaireTemplateWithRef[];
  emailTemplates: EmailTemplateWithRef[];
  taxRateEditor: {
    editingRateId: string | null;
    form: TaxRateFormState;
  };
}

const emptyTaxRateForm = (): TaxRateFormState => ({
  name: '',
  rate: '',
  inclusion: 'inclusive',
});

const REMOVED_FORM_TEMPLATE_NAMES = new Set(['test template for delete']);

const normalizeTemplateName = (name: string) => name.trim().toLowerCase();

const shouldRemoveTemplateByName = (name?: string | null) => {
  if (!name) {
    return false;
  }
  return REMOVED_FORM_TEMPLATE_NAMES.has(normalizeTemplateName(name));
};

const sanitizeContractTemplates = (templates: ContractTemplateWithRef[]) =>
  templates.filter(template => !shouldRemoveTemplateByName(template.name));

const sanitizeQuestionnaireTemplates = (templates: QuestionnaireTemplateWithRef[]) =>
  templates.filter(template => !shouldRemoveTemplateByName(template.name));

const loadStoredTemplateId = (key: string) => {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = window.localStorage.getItem(key);
  return value && value.length > 0 ? value : null;
};

const DEFAULT_QUOTE_TEMPLATE_ID_KEY = 'drahft_default_quote_template_id';
const DEFAULT_INVOICE_TEMPLATE_ID_KEY = 'drahft_default_invoice_template_id';

const initialState: SettingsTemplatesState = {
  userId: null,
  isSaving: false,
  isInitializing: false,
  isInitialized: false,
  error: null,
  taxRates: loadTaxRates(),
  productPackages: loadProductPackages(),
  discounts: [],
  paymentSchedules: DEFAULT_PAYMENT_SCHEDULES,
  quoteTemplates: DEFAULT_QUOTE_TEMPLATES,
  defaultQuoteTemplateId: null,
  invoiceTemplates: DEFAULT_INVOICE_TEMPLATES,
  defaultInvoiceTemplateId: null,
  contractTemplates: sanitizeContractTemplates(DEFAULT_CONTRACT_TEMPLATES),
  questionnaireTemplates: sanitizeQuestionnaireTemplates(DEFAULT_QUESTIONNAIRE_TEMPLATES),
  emailTemplates: DEFAULT_EMAIL_TEMPLATES,
  taxRateEditor: {
    editingRateId: null,
    form: emptyTaxRateForm(),
  },
};

const resolveSettings = (user: User): UserTemplateSettings => {
  const settings = user.templateSettings || {};
  return {
    ...settings,
    quoteTemplates: resolveTemplatesWithServerFallback(
      'drahft_quote_templates',
      DEFAULT_QUOTE_TEMPLATES,
      settings.quoteTemplates
    ),
    invoiceTemplates: resolveTemplatesWithServerFallback(
      'drahft_invoice_templates',
      DEFAULT_INVOICE_TEMPLATES,
      settings.invoiceTemplates
    ),
    contractTemplates: resolveTemplatesWithServerFallback(
      'drahft_contract_templates',
      DEFAULT_CONTRACT_TEMPLATES,
      settings.contractTemplates
    ),
    questionnaireTemplates: resolveTemplatesWithServerFallback(
      'drahft_questionnaire_templates',
      DEFAULT_QUESTIONNAIRE_TEMPLATES,
      settings.questionnaireTemplates
    ),
    emailTemplates: resolveTemplatesWithServerFallback(
      'drahft_email_templates',
      DEFAULT_EMAIL_TEMPLATES,
      settings.emailTemplates
    ),
    discounts: settings.discounts ?? loadInvoiceDiscounts(),
    defaultQuoteTemplateId:
      settings.defaultQuoteTemplateId === undefined
        ? loadStoredTemplateId(DEFAULT_QUOTE_TEMPLATE_ID_KEY)
        : settings.defaultQuoteTemplateId,
    defaultInvoiceTemplateId:
      settings.defaultInvoiceTemplateId === undefined
        ? loadStoredTemplateId(DEFAULT_INVOICE_TEMPLATE_ID_KEY)
        : settings.defaultInvoiceTemplateId,
  };
};

export const saveTemplateSettings = createAsyncThunk(
  'settingsTemplates/save',
  async (
    {
      paymentSchedules,
      ...templateSettings
    }: UserTemplateSettings & { paymentSchedules?: PaymentSchedule[] },
    { getState, dispatch, rejectWithValue }
  ) => {
    const { auth } = getState() as RootState;
    if (!auth.userProfile) return rejectWithValue('Sign in before saving templates');
    try {
      const updatedUser = await userService.updateProfile({
        ...(Object.keys(templateSettings).length > 0 ? { templateSettings } : {}),
        ...(paymentSchedules
          ? {
              fundamentalsSettings: { invoicePaymentSettings: { schedules: paymentSchedules } },
            }
          : {}),
      });
      if ((getState() as RootState).auth.userProfile?.id !== auth.userProfile.id) {
        return rejectWithValue('Your account changed while saving templates');
      }
      dispatch(setUserProfile(updatedUser));
      return updatedUser;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to save templates');
    }
  },
  {
    condition: (_, { getState }) => !(getState() as RootState).settingsTemplates.isSaving,
  }
);

export const initializeTemplateSettings = createAsyncThunk(
  'settingsTemplates/initialize',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const user = (getState() as RootState).auth.userProfile;
    if (!user) return rejectWithValue('Sign in to load templates');
    try {
      const resolved = resolveSettings(user);
      const missing = Object.fromEntries(
        Object.entries(resolved).filter(
          ([key]) => user.templateSettings?.[key as keyof UserTemplateSettings] === undefined
        )
      );
      const schedules = user.fundamentalsSettings?.invoicePaymentSettings?.schedules;
      if (Object.keys(missing).length > 0 || schedules === undefined) {
        const updatedUser = await userService.updateProfile({
          ...(Object.keys(missing).length > 0 ? { templateSettings: missing } : {}),
          ...(schedules === undefined
            ? {
                fundamentalsSettings: {
                  invoicePaymentSettings: { schedules: DEFAULT_PAYMENT_SCHEDULES },
                },
              }
            : {}),
        });
        if ((getState() as RootState).auth.userProfile?.id !== user.id) {
          return rejectWithValue('Your account changed while loading templates');
        }
        dispatch(setUserProfile(updatedUser));
      }
      [
        'drahft_quote_templates',
        'drahft_invoice_templates',
        'drahft_contract_templates',
        'drahft_questionnaire_templates',
        'drahft_email_templates',
        'drahft_invoice_discounts',
        DEFAULT_QUOTE_TEMPLATE_ID_KEY,
        DEFAULT_INVOICE_TEMPLATE_ID_KEY,
      ].forEach(key => {
        localStorage.removeItem(key);
      });
      return user.id;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load templates');
    }
  },
  {
    condition: (_, { getState }) => {
      const state = (getState() as RootState).settingsTemplates;
      return !state.isInitializing && !state.isInitialized;
    },
  }
);

const settingsTemplatesSlice = createSlice({
  name: 'settingsTemplates',
  initialState,
  reducers: {
    setTaxRates: (state, action: PayloadAction<TaxRate[]>) => {
      state.taxRates = action.payload;
      saveTaxRates(state.taxRates);
    },
    startCreateTaxRateEditor: state => {
      state.taxRateEditor.editingRateId = null;
      state.taxRateEditor.form = emptyTaxRateForm();
    },
    startEditTaxRateEditor: (state, action: PayloadAction<string>) => {
      const rate = state.taxRates.find(item => item.id === action.payload);
      if (!rate) return;

      state.taxRateEditor.editingRateId = rate.id;
      state.taxRateEditor.form = {
        name: rate.name,
        rate: String(rate.rate),
        inclusion: rate.inclusion,
      };
    },
    updateTaxRateForm: (
      state,
      action: PayloadAction<{ field: keyof TaxRateFormState; value: string }>
    ) => {
      const { field, value } = action.payload;
      if (field === 'inclusion') {
        state.taxRateEditor.form.inclusion = value as 'inclusive' | 'exclusive';
        return;
      }
      state.taxRateEditor.form[field] = value;
    },
    commitTaxRateForm: state => {
      const { editingRateId, form } = state.taxRateEditor;
      const parsedRate = parseFloat(form.rate);
      if (Number.isNaN(parsedRate) || parsedRate < 0 || !form.name.trim()) {
        return;
      }

      if (editingRateId) {
        state.taxRates = state.taxRates.map(rate =>
          rate.id === editingRateId
            ? {
                ...rate,
                name: form.name.trim(),
                rate: parsedRate,
                inclusion: form.inclusion,
              }
            : rate
        );
      } else {
        state.taxRates.push({
          id: `tax-${Date.now()}`,
          name: form.name.trim(),
          rate: parsedRate,
          inclusion: form.inclusion,
        });
      }

      saveTaxRates(state.taxRates);
      state.taxRateEditor.editingRateId = null;
      state.taxRateEditor.form = emptyTaxRateForm();
    },
    cancelTaxRateEditor: state => {
      state.taxRateEditor.editingRateId = null;
      state.taxRateEditor.form = emptyTaxRateForm();
    },
    deleteTaxRate: (state, action: PayloadAction<string>) => {
      state.taxRates = state.taxRates.filter(rate => rate.id !== action.payload);
      saveTaxRates(state.taxRates);
      if (state.taxRateEditor.editingRateId === action.payload) {
        state.taxRateEditor.editingRateId = null;
        state.taxRateEditor.form = emptyTaxRateForm();
      }
    },

    setDiscounts: (state, action: PayloadAction<Discount[]>) => {
      state.discounts = action.payload;
    },
    setProductPackages: (state, action: PayloadAction<ProductPackage[]>) => {
      state.productPackages = action.payload;
      saveProductPackages(state.productPackages);
    },
    upsertProductPackage: (state, action: PayloadAction<ProductPackage>) => {
      const existingIndex = state.productPackages.findIndex(
        productPackage => productPackage.id === action.payload.id
      );

      if (existingIndex >= 0) {
        state.productPackages[existingIndex] = action.payload;
      } else {
        state.productPackages.push(action.payload);
      }

      saveProductPackages(state.productPackages);
    },
    deleteProductPackage: (state, action: PayloadAction<string>) => {
      state.productPackages = state.productPackages.filter(
        productPackage => productPackage.id !== action.payload
      );

      state.quoteTemplates = state.quoteTemplates.map(template => ({
        ...template,
        packageIds: template.packageIds.filter(packageId => packageId !== action.payload),
      }));

      state.invoiceTemplates = state.invoiceTemplates.map(template => ({
        ...template,
        packageIds: template.packageIds.filter(packageId => packageId !== action.payload),
      }));

      saveProductPackages(state.productPackages);
    },
    upsertDiscountTemplate: (state, action: PayloadAction<Discount>) => {
      const existingIndex = state.discounts.findIndex(
        discount => discount.id === action.payload.id
      );

      if (existingIndex >= 0) {
        state.discounts[existingIndex] = action.payload;
        return;
      }

      state.discounts.push(action.payload);
    },
    deleteDiscountTemplate: (state, action: PayloadAction<string>) => {
      state.discounts = state.discounts.filter(discount => discount.id !== action.payload);
    },
    setPaymentSchedules: (state, action: PayloadAction<PaymentSchedule[]>) => {
      state.paymentSchedules = action.payload;
    },

    setQuoteTemplates: (state, action: PayloadAction<QuoteTemplateWithRef[]>) => {
      state.quoteTemplates = action.payload;
      if (!state.defaultQuoteTemplateId) {
        return;
      }

      const hasDefault = state.quoteTemplates.some(
        template => template.id === state.defaultQuoteTemplateId
      );
      if (!hasDefault) {
        state.defaultQuoteTemplateId = state.quoteTemplates[0]?.id ?? null;
      }
    },
    upsertQuoteTemplate: (state, action: PayloadAction<QuoteTemplateWithRef>) => {
      const existingIndex = state.quoteTemplates.findIndex(
        template => template.id === action.payload.id
      );

      if (existingIndex >= 0) {
        state.quoteTemplates[existingIndex] = action.payload;
        return;
      }

      state.quoteTemplates.push(action.payload);
    },
    deleteQuoteTemplate: (state, action: PayloadAction<string>) => {
      state.quoteTemplates = state.quoteTemplates.filter(
        template => template.id !== action.payload
      );
      if (state.defaultQuoteTemplateId === action.payload) {
        state.defaultQuoteTemplateId = state.quoteTemplates[0]?.id ?? null;
      }
    },
    setDefaultQuoteTemplateId: (state, action: PayloadAction<string | null>) => {
      if (!action.payload) {
        state.defaultQuoteTemplateId = null;
        return;
      }

      const templateExists = state.quoteTemplates.some(template => template.id === action.payload);
      state.defaultQuoteTemplateId = templateExists ? action.payload : null;
    },

    setInvoiceTemplates: (state, action: PayloadAction<InvoiceTemplateWithRef[]>) => {
      state.invoiceTemplates = action.payload;
      if (!state.defaultInvoiceTemplateId) {
        return;
      }

      const hasDefault = state.invoiceTemplates.some(
        template => template.id === state.defaultInvoiceTemplateId
      );
      if (!hasDefault) {
        state.defaultInvoiceTemplateId = state.invoiceTemplates[0]?.id ?? null;
      }
    },
    upsertInvoiceTemplate: (state, action: PayloadAction<InvoiceTemplateWithRef>) => {
      const existingIndex = state.invoiceTemplates.findIndex(
        template => template.id === action.payload.id
      );

      if (existingIndex >= 0) {
        state.invoiceTemplates[existingIndex] = action.payload;
        return;
      }

      state.invoiceTemplates.push(action.payload);
    },
    deleteInvoiceTemplate: (state, action: PayloadAction<string>) => {
      state.invoiceTemplates = state.invoiceTemplates.filter(
        template => template.id !== action.payload
      );
      if (state.defaultInvoiceTemplateId === action.payload) {
        state.defaultInvoiceTemplateId = state.invoiceTemplates[0]?.id ?? null;
      }
    },
    setDefaultInvoiceTemplateId: (state, action: PayloadAction<string | null>) => {
      if (!action.payload) {
        state.defaultInvoiceTemplateId = null;
        return;
      }

      const templateExists = state.invoiceTemplates.some(
        template => template.id === action.payload
      );
      state.defaultInvoiceTemplateId = templateExists ? action.payload : null;
    },

    setContractTemplates: (state, action: PayloadAction<ContractTemplateWithRef[]>) => {
      state.contractTemplates = sanitizeContractTemplates(action.payload);
    },
    upsertContractTemplate: (state, action: PayloadAction<ContractTemplateWithRef>) => {
      const existingIndex = state.contractTemplates.findIndex(
        template => template.id === action.payload.id
      );

      if (shouldRemoveTemplateByName(action.payload.name)) {
        return;
      }

      if (existingIndex >= 0) {
        state.contractTemplates[existingIndex] = action.payload;
        return;
      }

      state.contractTemplates.push(action.payload);
    },
    deleteContractTemplate: (state, action: PayloadAction<string>) => {
      state.contractTemplates = state.contractTemplates.filter(
        template => template.id !== action.payload
      );
    },

    setQuestionnaireTemplates: (state, action: PayloadAction<QuestionnaireTemplateWithRef[]>) => {
      state.questionnaireTemplates = sanitizeQuestionnaireTemplates(action.payload);
    },
    upsertQuestionnaireTemplate: (state, action: PayloadAction<QuestionnaireTemplateWithRef>) => {
      const existingIndex = state.questionnaireTemplates.findIndex(
        template => template.id === action.payload.id
      );

      if (shouldRemoveTemplateByName(action.payload.name)) {
        return;
      }

      if (existingIndex >= 0) {
        state.questionnaireTemplates[existingIndex] = action.payload;
        return;
      }

      state.questionnaireTemplates.push(action.payload);
    },
    deleteQuestionnaireTemplate: (state, action: PayloadAction<string>) => {
      state.questionnaireTemplates = state.questionnaireTemplates.filter(
        template => template.id !== action.payload
      );
    },

    updateQuestionnaireTemplateFieldOrder: (
      state,
      action: PayloadAction<{ templateId: string; fields: QuestionnaireField[] }>
    ) => {
      const template = state.questionnaireTemplates.find(
        item => item.id === action.payload.templateId
      );
      if (!template) {
        return;
      }

      template.fields = action.payload.fields;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(setUserProfile, (state, action) => {
        if (!action.payload) {
          Object.assign(state, initialState);
          return;
        }
        if (state.userId !== action.payload.id) {
          state.isInitialized = false;
          state.isInitializing = false;
          state.isSaving = false;
          state.error = null;
        }
        state.userId = action.payload.id;
        const settings = resolveSettings(action.payload);
        state.quoteTemplates = settings.quoteTemplates ?? DEFAULT_QUOTE_TEMPLATES;
        state.invoiceTemplates = settings.invoiceTemplates ?? DEFAULT_INVOICE_TEMPLATES;
        state.contractTemplates = sanitizeContractTemplates(
          settings.contractTemplates ?? DEFAULT_CONTRACT_TEMPLATES
        );
        state.questionnaireTemplates = sanitizeQuestionnaireTemplates(
          settings.questionnaireTemplates ?? DEFAULT_QUESTIONNAIRE_TEMPLATES
        );
        state.emailTemplates = settings.emailTemplates ?? DEFAULT_EMAIL_TEMPLATES;
        state.discounts = settings.discounts ?? [];
        state.defaultQuoteTemplateId = settings.defaultQuoteTemplateId ?? null;
        state.defaultInvoiceTemplateId = settings.defaultInvoiceTemplateId ?? null;
        state.paymentSchedules =
          action.payload.fundamentalsSettings?.invoicePaymentSettings?.schedules ??
          DEFAULT_PAYMENT_SCHEDULES;
      })
      .addCase(logout, () => ({ ...initialState }))
      .addCase(saveTemplateSettings.pending, state => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveTemplateSettings.fulfilled, state => {
        state.isSaving = false;
      })
      .addCase(saveTemplateSettings.rejected, (state, action) => {
        state.isSaving = false;
        state.error =
          typeof action.payload === 'string' ? action.payload : 'Failed to save templates';
      })
      .addCase(initializeTemplateSettings.pending, state => {
        state.isInitializing = true;
        state.error = null;
      })
      .addCase(initializeTemplateSettings.fulfilled, (state, action) => {
        if (state.userId !== action.payload) return;
        state.isInitializing = false;
        state.isInitialized = true;
      })
      .addCase(initializeTemplateSettings.rejected, (state, action) => {
        state.isInitializing = false;
        state.error =
          typeof action.payload === 'string' ? action.payload : 'Failed to load templates';
      });
  },
});

export const {
  setTaxRates,
  startCreateTaxRateEditor,
  startEditTaxRateEditor,
  updateTaxRateForm,
  commitTaxRateForm,
  cancelTaxRateEditor,
  deleteTaxRate,
  setDiscounts,
  setProductPackages,
  upsertProductPackage,
  deleteProductPackage,
  upsertDiscountTemplate,
  deleteDiscountTemplate,
  setPaymentSchedules,
  setQuoteTemplates,
  upsertQuoteTemplate,
  deleteQuoteTemplate,
  setDefaultQuoteTemplateId,
  setInvoiceTemplates,
  upsertInvoiceTemplate,
  deleteInvoiceTemplate,
  setDefaultInvoiceTemplateId,
  setContractTemplates,
  upsertContractTemplate,
  deleteContractTemplate,
  setQuestionnaireTemplates,
  upsertQuestionnaireTemplate,
  deleteQuestionnaireTemplate,
  updateQuestionnaireTemplateFieldOrder,
} = settingsTemplatesSlice.actions;

export default settingsTemplatesSlice.reducer;
